import React, { useState } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { List } from '@heartinternet/hi-ui';
import { ListItem } from '@heartinternet/hi-ui';
import { Card } from '@heartinternet/hi-ui';
import { FormField } from '@heartinternet/hi-ui';
import { Form } from '@heartinternet/hi-ui';
import './SoftTransferPage.css';

export interface SoftTransferPageProps {
  // State flags
  fromReseller?: boolean;
  transSuccess?: boolean;
  locked?: boolean;
  noSupport?: boolean;
  failure?: boolean;
  inAnotherAccount?: boolean;
  hosting?: boolean;
  delete?: boolean;
  refHasHosting?: boolean;
  badTag?: boolean;

  // Data
  domainName?: string;
  tag?: string;
  wwwDomain?: string;
  refParks?: Array<{ id: string; name: string }>;
}

// Matches typical domain shapes (label.tld, allows multi-level TLDs like .co.uk).
// Catches the most common silent-failure cases: empty input, missing TLD, spaces, junk chars.
const DOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

const validateDomain = (raw: string): string | null => {
  const domain = raw.trim().replace(/^www\./i, '');
  if (!domain) return 'Please enter a domain name.';
  if (!DOMAIN_REGEX.test(domain)) return 'Please enter a valid domain name (e.g. example.co.uk).';
  return null;
};

export const SoftTransferPage: React.FC<SoftTransferPageProps> = ({
  fromReseller = false,
  transSuccess,
  locked = false,
  noSupport = false,
  failure,
  inAnotherAccount = false,
  hosting = false,
  delete: deleteSuccess,
  refHasHosting = false,
  badTag = false,
  domainName = '',
  tag = '',
  wwwDomain = 'www.heartinternet.uk',
  refParks = [],
}) => {
  const [addError, setAddError] = useState<string | null>(null);
  // Once the user types in the add-reference field, the server-rendered
  // failure/success notices from the previous submit become stale — suppress
  // them so they don't sit alongside a fresh client-side error.
  const [addInteracted, setAddInteracted] = useState(false);
  const showServerAddNotice = !addInteracted;

  // Handler for mediate_transfer calls
  const handleMediateTransfer = (domain: string, includeHosting: boolean) => {
    // Call the global mediate_transfer function if it exists
    const win = window as typeof window & {
      mediate_transfer?: (domain: string, includeHosting: boolean) => void;
    };
    if (typeof win.mediate_transfer === 'function') {
      win.mediate_transfer(domain, includeHosting);
    }
  };

  const handleAddSubmit = (formData: FormData, event: React.FormEvent<HTMLFormElement>) => {
    // The Form molecule preventDefaults whenever onSubmit is provided, so we
    // submit the native form manually when validation passes. The native
    // submit() method does not refire the React onSubmit, so this terminates.
    const domain = String(formData.get('domain'));
    const error = validateDomain(domain);
    if (error) {
      setAddError(error);
      return;
    }
    setAddError(null);
    event.currentTarget.submit();
  };

  const addFailureNotice = (
    <>
      {showServerAddNotice && failure === true && (
        <>
          {inAnotherAccount ? (
            <>
              {hosting ? (
                <Notice variant="error" role="alert">
                  <Text variant="body">
                    The domain {domainName} is in another Heart Internet Account.
                  </Text>
                  <Text variant="body">
                    Click{' '}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMediateTransfer(domainName, true);
                      }}
                    >
                      here
                    </Link>{' '}
                    to contact the account owner and request that permission be given to us to
                    transfer the hosting package along with the domain name. (We may charge up to
                    £50.00+VAT for this service).
                  </Text>
                  <Text variant="body" inline>
                    Click{' '}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMediateTransfer(domainName, false);
                      }}
                    >
                      here
                    </Link>{' '}
                    to contact the account owner and request that only the domain name should be
                    transferred.
                  </Text>
                </Notice>
              ) : (
                <Notice variant="error" role="alert">
                  <Text variant="body" inline>
                    The domain {domainName} is in another Heart Internet Account; click{' '}
                    <Link
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleMediateTransfer(domainName, false);
                      }}
                    >
                      here
                    </Link>{' '}
                    to contact account owner.
                  </Text>
                </Notice>
              )}
            </>
          ) : (
            <Notice variant="error" role="alert">
              <Text variant="body" inline>
                Unable to add the reference.
              </Text>
            </Notice>
          )}
        </>
      )}
      {showServerAddNotice && failure === false && (
        <Notice variant="success" role="alert">
          <Text variant="body" inline>
            Your domain reference {domainName} has been set up. To activate a web hosting account
            for this reference{' '}
            <Link href={fromReseller ? '/manage/manage-reseller-add.cgi' : '/cp/manage/webhosting'}>
              click here
            </Link>
            .
          </Text>
        </Notice>
      )}
      {addError && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            {addError}
          </Text>
        </Notice>
      )}
    </>
  );

  const deleteOutcomeNotice = (
    <>
      {deleteSuccess === true && (
        <Notice variant="success" role="alert">
          <Text variant="body" inline>
            Reference has been removed.
          </Text>
        </Notice>
      )}
      {deleteSuccess === false && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            Unable to delete the reference.{' '}
            {refHasHosting && (
              <>
                You must remove any hosting packages first. To manage your web hosting accounts
                click <Link href="/manage/manage-packages.cgi">here</Link>.
              </>
            )}
          </Text>
        </Notice>
      )}
    </>
  );

  return (
    <div id="hi-ui-page-root" className="hi-ui-soft-transfer-page">
      {/* Page Title */}
      {fromReseller ? (
        <Text variant="heading-1">Domain References</Text>
      ) : (
        <Text variant="heading-1">Manage Domain References</Text>
      )}

      {/* Transfer-related notices (stay at top — outcomes of the transfer/purchase flow) */}
      {transSuccess === true && (
        <Notice variant="success" role="alert">
          <Text variant="body" inline>
            Transfer has been added to your <Link href="/manage/basket.cgi">basket</Link>. To
            finalise your transfer please <Link href="/manage/basket.cgi">checkout</Link>.
          </Text>
        </Notice>
      )}

      {transSuccess === false && !locked && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            Unable to transfer the domain into our system, if it&apos;s a{' '}
            <Text variant="body" as="span" bold>
              .uk
            </Text>{' '}
            please make sure the{' '}
            <Text variant="body" as="span" bold>
              IPSTAG
            </Text>{' '}
            is set to{' '}
            <Text variant="body" as="span" bold>
              &apos;HEARTINTERNET&apos; or &apos;EXTEND&apos;
            </Text>
            .
          </Text>
        </Notice>
      )}

      {locked && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            The domain appears to be locked at the registry. Please unlock the domain before
            attempting to transfer it.
          </Text>
        </Notice>
      )}

      {noSupport && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            Unable to transfer the domain into our system. We don&apos;t currently support that
            domain extension.
          </Text>
        </Notice>
      )}

      {/* badTag is also a transfer outcome (UK domain not on our IPSTAG), grouped with the others. */}
      {badTag && (
        <Notice variant="error" role="alert">
          <Text variant="body" inline>
            This (UK) domain is not on our tag (&apos;HEARTINTERNET&apos; or &apos;EXTEND&apos;),
            it&apos;s currently on {tag}.
          </Text>
        </Notice>
      )}

      {/* Information Section */}
      <Text variant="body">
        Domain name references are used on occasions where you need to activate web hosting before
        you transfer your domain name to Heart Internet or if you don&apos;t intend to/can&apos;t
        transfer your domain name to Heart Internet.
      </Text>

      <Text variant="heading-2">Why You Might Want to do This</Text>

      <List listStyle="disc" className="cp">
        <ListItem>
          You are transferring your hosting to Heart Internet and want to get your website ready
          before transferring your domain name.
        </ListItem>
        <ListItem>
          You want to manage your domain&apos;s DNS settings through another provider.
        </ListItem>
        <ListItem>
          You want to buy hosting with Heart Internet and haven&apos;t decided on a domain name yet.
        </ListItem>
      </List>

      <Text variant="body">
        Please be aware that un-associated domain references will be deleted after 24 hours.
      </Text>

      {/* Add Domain Reference Form */}
      <Text variant="heading-2">Add a new Domain Reference</Text>

      <Text variant="body">
        To create a domain reference simply enter the domain into the field below and click
        &quot;Add Reference&quot;, for example{' '}
        <Text variant="body" as="span" bold>
          {wwwDomain}
        </Text>{' '}
        would be{' '}
        <Text variant="body" as="span" bold>
          {wwwDomain.replace(/^www\./, '')}
        </Text>
      </Text>

      {/* Add-form outcome notices sit between the intro paragraph and the Card. */}
      {addFailureNotice}

      <Form action="/manage/soft_transfer.cgi" method="POST" onSubmit={handleAddSubmit}>
        {fromReseller && <input type="hidden" name="from_reseller" value="1" />}
        <Card variant="surface">
          <div className="hi-ui-soft-transfer-page__fields">
            <FormField label="Domain Name:" labelPosition="left" fullWidth>
              <Input
                type="text"
                name="domain"
                size="md"
                maxLength={200}
                fullWidth
                onChange={() => setAddInteracted(true)}
              />
            </FormField>
          </div>
          <div className="hi-ui-soft-transfer-page__submit">
            <input type="hidden" name="SU" value="ADDLINK" />
            <Button variant="primary" type="submit">
              Add Reference
            </Button>
          </div>
        </Card>
      </Form>

      {/* Delete Domain Reference Form */}
      {refParks && refParks.length > 0 ? (
        <>
          <Text variant="heading-2">Delete a Domain Reference</Text>

          <Text variant="body">
            Simply select the reference you would like to delete, you must unassociate the domain
            from any packages before it will appear in this list.
          </Text>

          {/* Delete-form outcome notices sit between the intro paragraph and the Card. */}
          {deleteOutcomeNotice}

          <Form action="/manage/soft_transfer.cgi" method="POST">
            {fromReseller && <input type="hidden" name="from_reseller" value="1" />}
            <Card variant="surface">
              <div className="hi-ui-soft-transfer-page__fields">
                <FormField label="Domain Name:" labelPosition="left" fullWidth>
                  <Select name="domain" size="md" fullWidth>
                    {refParks.map((refPark) => (
                      <option key={refPark.id} value={refPark.id}>
                        {refPark.name}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>
              <div className="hi-ui-soft-transfer-page__submit">
                <input type="hidden" name="SU" value="DELLINK" />
                <Button variant="warning" type="submit">
                  Delete Reference
                </Button>
              </div>
            </Card>
          </Form>
        </>
      ) : (
        // No references to delete — still surface a delete-outcome notice if
        // the user just removed their last one.
        deleteOutcomeNotice
      )}

      {/* Purchase or Transfer Link */}
      <Text variant="heading-2">Purchase or Transfer a Domain Reference</Text>

      <Text variant="body">
        Please{' '}
        <Link href={`/manage/soft_transfer.cgi?check=1${fromReseller ? ';from_reseller=1' : ''}`}>
          click here
        </Link>{' '}
        if you want to transfer or purchase one of your references.
      </Text>
    </div>
  );
};
